import { useRef, useState } from "react";
import Setup from "./pages/Setup.js";
import Login from "./pages/Login.js";
import Chat, { type ChatHandle } from "./pages/Chat.js";
import Settings from "./pages/Settings.js";
import Recovery from "./pages/Recovery.js";
import Authorize from "./pages/Authorize.js";
import { loadSession, type Session } from "./lib/session.js";

type View = "chat" | "settings";
type UnauthedView = "login" | "setup" | "recovery";

/** Reads `?code=` once at startup — this app has no real router (see the
 * handoff/CLAUDE.md's view-switching convention above), so a device-
 * authorization deep link (`/authorize?code=XXXX-XXXX`, printed by `pnpm
 * --filter mcp login`) is detected the same ad-hoc way any other one-off
 * "initial view" decision already is in this file. Read once into state
 * (not on every render) so navigating within the app afterward doesn't keep
 * re-triggering it. */
function readPendingAuthCodeFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("code");
}

/**
 * Revision 4: routes to Login/Setup instead of Pairing when there is no
 * session. There is no separate "does an account exist yet" probe endpoint
 * (see the handoff's Files-to-touch table — only salt/register/login exist);
 * instead the unauthenticated view defaults to Login (the common case after
 * the one-time Setup has already happened) with a link to Setup, and Setup
 * itself redirects back to Login if the server reports the account already
 * exists (403 `registration_closed_account_already_exists`).
 *
 * Revision 5, Decision 4: adds a `Recovery` view ("Forgot password?" from
 * Login) alongside Setup/Login.
 */
export default function App() {
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [view, setView] = useState<View>("chat");
  const [unauthedView, setUnauthedView] = useState<UnauthedView>("login");
  // Carried across the Login/Setup flow below (state, not re-read from the
  // URL) so a device-authorization code isn't lost if the user has to log in
  // first — see the class-level comment on `readPendingAuthCodeFromUrl`.
  const [pendingAuthCode, setPendingAuthCode] = useState<string | null>(() => readPendingAuthCodeFromUrl());
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const [chatMenuState, setChatMenuState] = useState({ searchVisible: false, showAttachments: false });
  const chatRef = useRef<ChatHandle>(null);

  if (!session) {
    return (
      <div className="mx-auto flex min-h-screen max-w-[640px] flex-col">
        <div className="flex flex-1 flex-col gap-4 p-4">
          {unauthedView === "login" && (
            <Login
              onSwitchToSetup={() => setUnauthedView("setup")}
              onSwitchToRecovery={() => setUnauthedView("recovery")}
              onLoggedIn={setSession}
            />
          )}
          {unauthedView === "setup" && (
            <Setup onSwitchToLogin={() => setUnauthedView("login")} onCreated={setSession} />
          )}
          {unauthedView === "recovery" && <Recovery onSwitchToLogin={() => setUnauthedView("login")} />}
        </div>
      </div>
    );
  }

  if (pendingAuthCode) {
    return (
      <div className="mx-auto flex min-h-screen max-w-[640px] flex-col">
        <div className="flex flex-1 flex-col gap-4 p-4">
          <Authorize
            session={session}
            initialCode={pendingAuthCode}
            onDone={() => {
              setPendingAuthCode(null);
              // Drop `?code=` from the address bar now that it's been acted
              // on (or dismissed) — purely cosmetic, avoids re-triggering
              // this branch on a later refresh of the same tab.
              window.history.replaceState({}, "", window.location.pathname);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    // Chat/Settings shell: at least half the viewport wide (and never narrower
    // than the old 640px on smaller screens, nor wider than the screen itself).
    // `h-screen`, not `min-h-screen`: the whole flex-1 chain down to the
    // message list's `overflow-y-auto` needs a FIXED (not just minimum)
    // height to actually bound and clip — with `min-h-screen`, once messages
    // overflowed, the page itself just grew taller and the document scrolled
    // instead of the message list's own scroll container, so `handleScroll`
    // (bound to that div's `onScroll`) never fired and scroll-up pagination
    // never triggered.
    <div className="mx-auto flex h-screen w-[max(640px,50vw)] max-w-full flex-col">
      <nav className="flex gap-2 border-b border-slate-800 px-4 py-3">
        <button
          type="button"
          onClick={() => setView("chat")}
          className={`cursor-pointer rounded-full border px-3.5 py-1.5 ${
            view === "chat" ? "border-blue-600 bg-blue-600" : "border-slate-700 bg-transparent"
          } text-slate-200`}
        >
          Chat
        </button>
        <button
          type="button"
          onClick={() => setView("settings")}
          className={`cursor-pointer rounded-full border px-3.5 py-1.5 ${
            view === "settings" ? "border-blue-600 bg-blue-600" : "border-slate-700 bg-transparent"
          } text-slate-200`}
        >
          Settings
        </button>
        {/* "⋮" lives here (next to the Chat/Settings tabs, outside the chat
            area itself), not inside Chat.tsx's own toolbar — Chat exposes
            toggleSearch/toggleAttachments via a ref so this menu can drive
            it without lifting all of Chat's state up into App. */}
        {view === "chat" && (
          <div className="relative ml-auto">
            <button
              type="button"
              onClick={() => setChatMenuOpen((v) => !v)}
              aria-label="Chat options"
              className="cursor-pointer rounded-lg border border-slate-700 bg-transparent px-3 py-1.5 text-lg leading-none text-slate-200"
            >
              ⋮
            </button>
            {chatMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setChatMenuOpen(false)} />
                <div className="absolute right-0 top-full z-20 mt-1 w-44 overflow-hidden rounded-lg border border-slate-700 bg-gray-900 shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      chatRef.current?.toggleSearch();
                      setChatMenuOpen(false);
                    }}
                    className="block w-full cursor-pointer px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
                  >
                    {chatMenuState.searchVisible ? "Hide search" : "Search"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      chatRef.current?.toggleAttachments();
                      setChatMenuOpen(false);
                    }}
                    className="block w-full cursor-pointer px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
                  >
                    {chatMenuState.showAttachments ? "Back to chat" : "Attachments"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </nav>
      <div className="flex flex-1 flex-col gap-4 p-4">
        {/* Both stay mounted (toggled via CSS, not conditional rendering) so
            switching tabs doesn't tear down Chat's message state and
            WebSocket connection — remounting it caused a brief empty-list
            flash on every trip back from Settings. */}
        <div className={view === "chat" ? "flex flex-1 flex-col" : "hidden"}>
          <Chat ref={chatRef} session={session} onMenuStateChange={setChatMenuState} />
        </div>
        <div className={view === "settings" ? "flex flex-1 flex-col" : "hidden"}>
          <Settings session={session} onRevoked={() => setSession(null)} />
        </div>
      </div>
    </div>
  );
}
